import { useQuery } from "@tanstack/react-query";
import { PowerOff } from "lucide-react";
import { EmptyState } from "@order-analyzer/ui";
import { DEFAULT_FEATURE_FLAGS, FEATURE_DEFINITIONS, FEATURE_KEYS, type FeatureFlags, type FeatureKey } from "@order-analyzer/shared";
import { api } from "../api.js";
import { useHasPermission } from "./permissions.js";

export const FEATURE_FLAGS_QUERY_KEY = ["features"] as const;

/**
 * Funktionsschalter der Instanz (Administration -> Funktionen). Bis zur ersten Antwort gelten die
 * Standardwerte (alles aktiv), damit die Navigation beim Laden nicht flackert; ein deaktiviertes
 * Modul verschwindet dann nach der Antwort. Die eigentliche Sperre setzt die API durch.
 */
export function useFeatureFlags(): FeatureFlags {
  const query = useQuery({ queryKey: FEATURE_FLAGS_QUERY_KEY, queryFn: () => api.get<FeatureFlags>("/features"), staleTime: 60_000 });
  // Fehlende oder unerwartete Werte (ältere API, Teilantwort) fallen auf den Standard zurück.
  const flags = { ...DEFAULT_FEATURE_FLAGS };
  for (const key of FEATURE_KEYS) if (typeof query.data?.[key] === "boolean") flags[key] = query.data[key];
  return flags;
}

/** Hinweis statt Seite, wenn eine Modul-URL direkt aufgerufen wird, das Modul aber aus ist. */
export function RequireFeature({ feature, children }: { feature: FeatureKey; children: React.ReactNode }) {
  const flags = useFeatureFlags();
  const canConfigure = useHasPermission("config:write");
  if (flags[feature]) return <>{children}</>;
  return (
    <EmptyState
      icon={<PowerOff className="h-6 w-6" />}
      title={`${FEATURE_DEFINITIONS[feature].label} ist deaktiviert`}
      description={
        canConfigure
          ? "Sie können die Funktion unter Administration -> Funktionen aktivieren."
          : "Diese Funktion ist auf dieser Instanz derzeit nicht freigeschaltet. Wenden Sie sich bei Bedarf an einen Administrator."
      }
    />
  );
}
